import { parseColor } from '@ark-ui/react'
import { isColorWhite } from '@qr-front/ui/utils'
import {
	DEFAULT_FRAME_FONT,
	FRAME_TYPE_OPTION_LIST,
	TQrFrameProps,
} from 'features/generator/qrFrames'
import { chain } from 'radash'
import { renderToString } from 'react-dom/server'

import { setSvgAttribute } from '@/utils'

import { TRANSPARENT_COLOR } from '../../../../qrFrames/qrFramesConstants'
import { DefaultFrame } from './DefaultFrame'
import { TQrDesign } from './qrImageTypes'
import { addEncodedFontToSvg } from './utils/addEncodedFontToSvg'
import { createQrPatternInstance } from './utils/createQrPatternInstance'
import { improveQrSvgCompatibility } from './utils/improveQrSvgCompatibility'
import { measureFitTextSize } from './utils/measureFitTextSize'

export const CONTRAST_BACKGROUND_COLOR = '#CDD0DB'

/**
 * Fixes QRs created in legacy app with default frame and no pattern background showing a black background instead of white. This is needed for backwards compatibility.
 */
export const fixIncompatibleLegacyQrDesign = (
	qrDesign: TQrDesign,
): TQrDesign => {
	const fixedBackgroundOptions = {
		...qrDesign.backgroundOptions,
		color: qrDesign.backgroundOptions.color || '#FFFFFF',
	}
	return { ...qrDesign, backgroundOptions: fixedBackgroundOptions }
}

export const areAllColorsWhite = (qrDesign: TQrDesign): boolean => {
	if (!qrDesign) {
		return false
	}

	const dotsColor = qrDesign?.dotsOptions?.color
	const cornerSquareColor = qrDesign?.cornersSquareOptions?.color
	const cornerDotColor = qrDesign?.cornersDotOptions?.color
	const backgroundColor = qrDesign?.backgroundOptions?.color

	// Also check if background is transparent or white
	const isWhiteOrTransparentBg =
		!backgroundColor || isColorWhite(parseColor)(backgroundColor)

	return (
		[dotsColor, cornerSquareColor, cornerDotColor].every((color) =>
			color ? isColorWhite(parseColor)(color) : false,
		) && isWhiteOrTransparentBg
	)
}

/**
 * Ensures QR code visibility by applying a contrast background when the QR has white colors.
 * This prevents white QR codes from being invisible on white backgrounds.
 */
export const ensureQrVisibility = (qrDesign: TQrDesign): TQrDesign => {
	// Check if any of the main QR elements are white colored
	const allWhiteColors = areAllColorsWhite(qrDesign)

	if (!allWhiteColors) {
		return qrDesign
	}

	return {
		...qrDesign,
		backgroundOptions: {
			...qrDesign.backgroundOptions,
			color: CONTRAST_BACKGROUND_COLOR,
		},
	}
}

export const createQrImageBuilder = async (
	_qrDesign: TQrDesign,
	logoFile?: File | null,
) => {
	const qrDesign = fixIncompatibleLegacyQrDesign(ensureQrVisibility(_qrDesign))

	const { qrPattern, isLogoDropped } = await createQrPatternInstance(
		qrDesign,
		logoFile,
	)

	const getPatternSvg = async () => {
		const blob = await qrPattern.getRawData('svg')
		if (!(blob instanceof Blob)) {
			throw new Error('Failed to generate QR code SVG')
		}
		const originalSvg = await blob.text()
		const improvedSvg = improveQrSvgCompatibility(originalSvg)
		const parser = new DOMParser()
		const document = parser.parseFromString(improvedSvg, 'image/svg+xml')
		const svg = document.querySelector('svg')
		if (!svg) {
			throw new Error('Failed to parse QR code SVG')
		}
		return svg.outerHTML
	}

	const getSvg = async (
		{
			disablePaddingOnDefaultFrameIfNoBg,
		}: {
			disablePaddingOnDefaultFrameIfNoBg: boolean
		} = { disablePaddingOnDefaultFrameIfNoBg: false },
	) => {
		const patternMarkup = await getPatternSvg()

		const patternBackgroundColor = qrDesign.backgroundOptions.color!

		const isDefaultFrame = qrDesign.frame?.type === 'default'

		// For the default frame, only add background with padding to the pattern
		if (isDefaultFrame) {
			const disablePadding =
				disablePaddingOnDefaultFrameIfNoBg &&
				(isColorWhite(parseColor)(patternBackgroundColor) ||
					patternBackgroundColor === TRANSPARENT_COLOR)

			return renderToString(
				DefaultFrame({
					patternMarkup,
					backgroundColor: patternBackgroundColor,
					enablePadding: !disablePadding,
				}),
			)
		}

		const selectedFrameOption = FRAME_TYPE_OPTION_LIST.find(
			({ value }: { value: string }) => value === qrDesign.frame?.type,
		)

		if (!selectedFrameOption) {
			throw new Error(`Frame type ${qrDesign.frame?.type} not found`)
		}

		const Frame = selectedFrameOption.Frame

		// Get text constraints from frame options
		const textMaxWidth = selectedFrameOption?.customMaxWidth || 52
		const textMaxSize = selectedFrameOption?.customMaxFontSize || 9
		const fontFamily =
			selectedFrameOption?.customFontFamily || DEFAULT_FRAME_FONT

		// Measure text size
		const fontSize = await measureFitTextSize({
			text: qrDesign.frame?.text || '',
			maxWidth: textMaxWidth,
			maxSize: textMaxSize,
			fontFamily,
		})

		// Render the frame component
		const frameProps: TQrFrameProps = {
			previewMode: false,
			patternMarkup,
			text: qrDesign.frame?.text || '',
			fontSize,
			frameColor: qrDesign.frame.color,
			textColor: qrDesign.frame.textColor,
			frameBackgroundColor: qrDesign.frame.background,
			patternBackgroundColor,
		}

		return renderToString(Frame(frameProps))
	}

	const getOptimizedSvg = async () => {
		const frameData = FRAME_TYPE_OPTION_LIST.find(
			(item: { value: string }) => item.value === qrDesign.frame?.type,
		)
		const getOptimizedDefaultFrameSvg = async () =>
			chain(
				(svgString) =>
					setSvgAttribute({
						svg: svgString,
						attributeName: 'width',
						attributeValue: 810,
					}),
				(svgString) =>
					setSvgAttribute({
						svg: svgString,
						attributeName: 'height',
						attributeValue: 810,
					}),
			)(await getSvg())
		if (!frameData) {
			return getOptimizedDefaultFrameSvg()
		}
		const font = frameData?.customFontFamily || DEFAULT_FRAME_FONT
		return chain(
			(svgString) =>
				setSvgAttribute({
					svg: svgString,
					attributeName: 'width',
					attributeValue: 320,
				}),
			(svgString) =>
				setSvgAttribute({
					svg: svgString,
					attributeName: 'height',
					attributeValue: 420,
				}),
			(svgString) => addEncodedFontToSvg(svgString, font),
		)(await getSvg())
	}

	return {
		getPatternSvg,
		getSvg,
		getOptimizedSvg,
		isLogoDropped,
	}
}
